import React from 'react';

// ════════════════════════════════════════════════════════════════════════════
// iconesNotificacoes.jsx — r212
// ────────────────────────────────────────────────────────────────────────────
// Ícones SVG + rótulo + cor de cada tipo de notificação. Substitui os emojis
// que eram usados no pop-up de avisos e no sininho: emoji muda de desenho a
// cada sistema operacional e não aceita cor, então não dava pra montar uma
// legenda confiável.
//
// Convenção (mesma do iconesRotinas.jsx): viewBox 0 0 16 16 e `currentColor`,
// pra cor vir do contexto onde o ícone é renderizado.
//
// Uso:
//   {iconeDaNotificacao('COMENTARIO', { width: 18, height: 18, color: '#003399' })}
//   TIPOS_NOTIFICACAO.map(t => ...)   // pra montar a legenda/filtro
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul: '#003399', roxo: '#7c3aed', verde: '#10b981',
  amarelo: '#d97706', vermelho: '#e53935', muted: '#888',
};

const SVGS = {
  // Documento com "+" — tarefa nova
  TAREFA_NOVA: (
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M3 2.5A1.5 1.5 0 014.5 1H9l4 4v8.5A1.5 1.5 0 0111.5 15h-7A1.5 1.5 0 013 13.5v-11z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M9 1v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M8 8v4M6 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // Pessoa com seta — tarefa atribuída
  TAREFA_ATRIBUIDA: (
    <svg viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="4.5" r="2.5" fill="currentColor"/>
      <path d="M1.5 13.5c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M11 6.5h4M13 4.5l2 2-2 2"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Check dentro de círculo — subtarefa concluída
  TAREFA_FILHA_CONCLUIDA: (
    <svg viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M5.2 8.2l2 2 3.6-4"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Relógio — prazo próximo
  DEADLINE_PROXIMA: (
    <svg viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8.5" r="5.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 5.5v3.2l2.1 1.3"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.5 1.5L3 3.2M10.5 1.5L13 3.2"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // Balão de fala — comentário / menção
  COMENTARIO: (
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M2 4.5A1.5 1.5 0 013.5 3h9A1.5 1.5 0 0114 4.5v5A1.5 1.5 0 0112.5 11H7l-3.5 3v-3A1.5 1.5 0 012 9.5v-5z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M5 6h6M5 8.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // Raio — priorização solicitada
  PRIORIDADE_SOLICITADA: (
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M9 1L3.5 9H7.5l-.5 6L12.5 7H8.5L9 1z"
            fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
    </svg>
  ),
};

// Sino — fallback pra tipo novo que ainda não ganhou desenho próprio
const SVG_GENERICO = (
  <svg viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5a4 4 0 014 4v3l1.3 2.2H2.7L4 8.5v-3a4 4 0 014-4z"
          stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M6.4 12.8a1.7 1.7 0 003.2 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// ────────────────────────────────────────────────────────────────────────────
// Catálogo — ordem aqui é a ordem em que a legenda aparece na tela.
// `codigo` casa com notificacoes.tipo; 'OUTRO' agrupa tipos sem desenho.
// ────────────────────────────────────────────────────────────────────────────
export const TIPOS_NOTIFICACAO = [
  { codigo: 'TAREFA_NOVA',            label: 'Tarefa nova',        cor: COR.azul     },
  { codigo: 'TAREFA_ATRIBUIDA',       label: 'Atribuída a você',   cor: COR.roxo     },
  { codigo: 'TAREFA_FILHA_CONCLUIDA', label: 'Subtarefa concluída', cor: COR.verde   },
  { codigo: 'DEADLINE_PROXIMA',       label: 'Prazo próximo',      cor: COR.amarelo  },
  { codigo: 'COMENTARIO',             label: 'Comentário',         cor: COR.azul     },
  { codigo: 'PRIORIDADE_SOLICITADA',  label: 'Priorização pedida', cor: COR.vermelho },
  { codigo: 'OUTRO',                  label: 'Outros avisos',      cor: COR.muted    },
];

const POR_CODIGO = TIPOS_NOTIFICACAO.reduce((acc, t) => { acc[t.codigo] = t; return acc; }, {});

// Normaliza um tipo vindo do banco pro código do catálogo ('OUTRO' se não tem).
export function tipoNormalizado(tipo) {
  return SVGS[tipo] ? tipo : 'OUTRO';
}

// Metadados (label + cor) de um tipo — nunca retorna undefined.
export function visualDaNotificacao(tipo) {
  return POR_CODIGO[tipoNormalizado(tipo)] || POR_CODIGO.OUTRO;
}

// ────────────────────────────────────────────────────────────────────────────
// iconeDaNotificacao(tipo, options)
//   options.width / height — tamanho (default 16)
//   options.color — cor do stroke/fill (default 'currentColor')
// ────────────────────────────────────────────────────────────────────────────
export function iconeDaNotificacao(tipo, { width = 16, height = 16, color = 'currentColor' } = {}) {
  const svg = SVGS[tipo] || SVG_GENERICO;
  return React.cloneElement(svg, {
    width, height, style: { color, flexShrink: 0 },
  });
}
